import { Suspense, lazy, useId, useMemo, useState } from 'react'
import { Expand } from 'lucide-react'
import { Button, Dialog, IconButton, SegmentedControl } from './common'
import { brandingToCssVars, resolveBranding } from '@feed-widget/branding'
import { FeedCard } from '@feed-widget/components/card/FeedCard'
import { WinningTicketCard } from '@feed-widget/components/card/WinningTicketCard'
import { VideoIdScopeContext, videoIdInScope } from '@feed-widget/components/card/videoIdScopeContext'
import { useVideoPlaybackStore } from '@feed-widget/stores/videoPlaybackStore'
import WidgetSurfacePreview, { WidgetPreviewProviders } from './design/WidgetSurfacePreview'
import { buildPreviewShowcase } from './design/previewShowcase'
import { getPreviewSelections, normalizeFeedPreviewItem, previewItemHasContent } from '../utils/feedPreviewItem'

import styles from './BrandedFeedPreview.module.css'

const CompactCard = lazy(() =>
  import('@feed-widget/components/carousel/CompactCard').then((module) => ({ default: module.CompactCard }))
)

const PREVIEW_MODES = [
  { key: 'feed', label: 'Full Feed' },
  { key: 'card', label: 'Feed Card' },
  { key: 'compact', label: 'Compact Card' },
]

// Panel and dialog players of one preview instance get distinct store keys.
function previewVideoScope(instanceId, expanded) {
  return `studio-preview${instanceId}${expanded ? '-expanded' : '-panel'}`
}

function getPreviewProvider(surface) {
  const configuredProvider = surface?.config?.provider
  if (configuredProvider) return String(configuredProvider).toLowerCase()

  const slug = String(surface?.slug || '').toLowerCase()
  if (slug === 'markets' || slug === 'og') return 'og'
  if (slug === 'sports' && surface?.name === 'Crypto.com Sports') return 'cryptodotcom'
  return ''
}

function hasPreviewContent(source) {
  return previewItemHasContent(source)
}

function DraftPreviewPlaceholder({ testId, showCardHeader = true }) {
  return (
    <div
      className={styles.draftPreviewEmpty}
      data-testid={`${testId}-empty`}
      role="img"
      aria-label="Post preview placeholder"
    >
      <div className={styles.draftPreviewCard} aria-hidden="true">
        {showCardHeader ? (
          <div className={styles.draftPreviewHeader}>
            <span className={styles.draftPreviewAvatar} />
            <span className={styles.draftPreviewLineMedium} />
          </div>
        ) : null}
        <div className={styles.draftPreviewMedia}>
          <span className={styles.draftPreviewGlyph} />
        </div>
        <div className={styles.draftPreviewBody}>
          <span className={styles.draftPreviewLineLong} />
          <span className={styles.draftPreviewLineShort} />
          <div className={styles.draftPreviewActions}>
            <span />
            <span />
          </div>
        </div>
      </div>
    </div>
  )
}


export default function BrandedFeedPreview({
  branding,
  item,
  title = 'Live Feed Preview',
  eyebrow = 'Preview',
  chrome = true,
  showModeSelector = false,
  allowFullscreen = false,
  showCardHeader = true,
  initialMode,
  displayType,
  surface = null,
  testId = 'branded-feed-preview',
}) {
  const [mode, setMode] = useState(() => initialMode || (item ? 'card' : 'feed'))
  const [isFullscreen, setIsFullscreen] = useState(false)
  // Two previews of one post can be mounted at once (Manage posts hover
  // preview + inspector drawer, inline panel + expanded dialog). Each panel
  // scopes its video ids so they never share a videoPlaybackStore entry.
  const instanceId = useId()
  // The dialog mounts a second player for the same post, so this instance's
  // own panel player must not keep running underneath it. Only that player is
  // touched: other previews on the page (hover preview, another drawer) keep
  // playing. Mirrors togglePlay's pause branch without setActiveVideo(null),
  // which would pause every registered video. Closing needs nothing: the
  // dialog player unmounts and unregisters itself.
  const openFullscreen = () => {
    const store = useVideoPlaybackStore.getState()
    if (videoIdInScope(previewVideoScope(instanceId, false), store.activeVideoId)) {
      store.setManuallyPaused(store.activeVideoId, true)
      useVideoPlaybackStore.setState({ activeVideoId: null })
    }
    setIsFullscreen(true)
  }
  const closeFullscreen = () => setIsFullscreen(false)
  const resolvedBranding = useMemo(() => resolveBranding(branding || {}), [branding])
  const cssVars = useMemo(() => brandingToCssVars(resolvedBranding), [resolvedBranding])
  const previewProvider = useMemo(() => getPreviewProvider(surface), [surface])
  const previewShowcase = useMemo(
    () => buildPreviewShowcase(resolvedBranding.buttonText, { provider: previewProvider }),
    [resolvedBranding.buttonText, previewProvider]
  )
  const previewItem = normalizeFeedPreviewItem(item || previewShowcase.feedCard)
  const itemHasPreviewContent = item ? hasPreviewContent(previewItem) : true
  const itemHasSelections = item ? getPreviewSelections(previewItem).length > 0 : false
  const compactPreviewItem = normalizeFeedPreviewItem(previewShowcase.compactCard)
  const surfacePreviewItems = useMemo(
    () => {
      if (!item) {
        return [...previewShowcase.feedItems, ...previewShowcase.compactItems].map((preview) => normalizeFeedPreviewItem(preview))
      }

      const primary = normalizeFeedPreviewItem(item)
      if (!hasPreviewContent(primary)) return []

      return [primary]
    },
    [item, previewShowcase]
  )
  const bannerImageUrl = resolvedBranding.banner?.imageUrl || ''
  const hasBannerImage = Boolean(bannerImageUrl)
  const bannerAltText = resolvedBranding.banner?.altText || 'Feed banner preview'
  const dialogLabel = `${title} full preview`

  function renderWithWidgetContext(cardItem, content, videoScope) {
    return (
      <WidgetPreviewProviders
        branding={resolvedBranding}
        displayType={displayType || surface?.display_type || 'story_block'}
        surface={surface}
        items={[cardItem]}
        showBettingChrome={getPreviewSelections(cardItem).length > 0}
      >
        <VideoIdScopeContext.Provider value={videoScope}>
          {content}
        </VideoIdScopeContext.Provider>
      </WidgetPreviewProviders>
    )
  }

  function renderFeedCard(cardItem, extraProps = {}, videoScope = '') {
    if (cardItem.content_origin === 'winning_ticket') {
      return renderWithWidgetContext(cardItem, (
        <WinningTicketCard
          item={cardItem}
          index={0}
          viewerIdentified
          isFollowing={() => false}
          isFollowPending={() => false}
          onToggleFollow={() => {}}
        />
      ), videoScope)
    }
    return renderWithWidgetContext(cardItem, (
      <FeedCard
        item={cardItem}
        index={0}
        darkModeOverride={resolvedBranding.darkMode}
        showHeader={showCardHeader}
        {...extraProps}
      />
    ), videoScope)
  }

  function renderCompactCard(cardItem, videoScope = '') {
    return renderWithWidgetContext(cardItem, (
      <div className={styles.compactPreview}>
        <CompactCard
          item={cardItem}
          index={0}
          darkModeOverride={resolvedBranding.darkMode}
        />
      </div>
    ), videoScope)
  }

  function renderDraftPreviewPlaceholder() {
    return <DraftPreviewPlaceholder testId={testId} showCardHeader={showCardHeader} />
  }

  function renderFeedShell(videoScope = '') {
    return (
      <div className={styles.feedShellPreview}>
        <div className={styles.feedShellBanner}>
          {hasBannerImage ? (
            <img src={bannerImageUrl} alt={bannerAltText} />
          ) : (
            <div className={styles.feedShellBannerPlaceholder}>Banner goes here</div>
          )}
        </div>
        <div className={styles.feedShellList}>
          {renderFeedCard(
            item ? previewItem : previewShowcase.feedCard,
            item ? {} : { canFollow: true, isFollowing: false },
            videoScope,
          )}
        </div>
      </div>
    )
  }

  function renderPanel({ expanded = false, panelTestId = testId } = {}) {
    const panelClassName = [
      styles.previewPanel,
      !chrome ? styles.previewPanelBare : '',
      expanded ? styles.previewPanelExpanded : '',
    ].filter(Boolean).join(' ')
    const frameClassName = [
      styles.previewFrame,
      !chrome ? styles.previewFrameBare : '',
      expanded ? styles.previewFrameExpanded : '',
    ].filter(Boolean).join(' ')
    const canvasClassName = [
      styles.previewCanvas,
      !chrome ? styles.previewCanvasBare : '',
      expanded ? styles.previewCanvasExpanded : '',
    ].filter(Boolean).join(' ')
    const showHeader = chrome && (eyebrow || title || allowFullscreen || showModeSelector)
    const bodyClassName = styles.previewBody
    const videoScope = previewVideoScope(instanceId, expanded)
    const showBareExpandButton = allowFullscreen && !chrome && !expanded
    return (
      <aside className={panelClassName}>
        {showHeader ? (
          <div className={styles.previewHeader}>
            <div className={styles.previewHeaderTop}>
              <div>
                {eyebrow ? <p className={styles.previewEyebrow}>{eyebrow}</p> : null}
                {title ? <h3 className={styles.previewTitle}>{title}</h3> : null}
              </div>
              {allowFullscreen && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={expanded ? closeFullscreen : openFullscreen}
                >
                  {expanded ? 'Close full preview' : 'Open full preview'}
                </Button>
              )}
            </div>
            {showModeSelector && (
              <div className={styles.previewModes}>
                <SegmentedControl
                  items={PREVIEW_MODES}
                  value={mode}
                  onValueChange={setMode}
                  label="Preview mode"
                />
              </div>
            )}
          </div>
        ) : null}

        {showBareExpandButton ? (
          <div className={styles.previewBareActions}>
            <IconButton
              variant="ghost"
              label="Expand preview"
              onClick={openFullscreen}
            >
              <Expand size={16} aria-hidden="true" />
            </IconButton>
          </div>
        ) : null}

        <div className={bodyClassName}>
          <div
            className={frameClassName}
            data-dark-mode={resolvedBranding.darkMode ? 'true' : 'false'}
            data-testid={panelTestId}
            style={cssVars}
          >
            <div className={canvasClassName}>
              <Suspense fallback={<div className={styles.previewLoading}>Loading preview...</div>}>
                {/* Surface previews keep public video ids: their surfaces compute
                    `${postId}-0` themselves, so a scope would desync them. */}
                {displayType ? (
                  item && !itemHasPreviewContent ? (
                    renderDraftPreviewPlaceholder()
                  ) : (
                    <WidgetSurfacePreview
                      branding={resolvedBranding}
                      displayType={displayType}
                      surface={surface}
                      items={surfacePreviewItems}
                      fallback={<div className={styles.previewLoading}>Loading preview...</div>}
                      showBettingChrome={itemHasSelections ? true : undefined}
                    />
                  )
                ) : mode === 'compact' ? (
                  item && !itemHasPreviewContent
                    ? renderDraftPreviewPlaceholder()
                    : renderCompactCard(item ? previewItem : compactPreviewItem, videoScope)
                ) : mode === 'feed' ? (
                  item && !itemHasPreviewContent
                    ? renderDraftPreviewPlaceholder()
                    : renderFeedShell(videoScope)
                ) : (
                  item && !itemHasPreviewContent
                    ? renderDraftPreviewPlaceholder()
                    : renderFeedCard(previewItem, item ? {} : { canFollow: true, isFollowing: false }, videoScope)
                )}
              </Suspense>
            </div>
          </div>
        </div>
      </aside>
    )
  }

  return (
    <>
      {renderPanel()}
      {allowFullscreen ? (
        <Dialog
          open={isFullscreen}
          title={chrome ? dialogLabel : title}
          size="media"
          hideCloseButton={chrome}
          contentClassName={styles.previewDialogContent}
          onClose={closeFullscreen}
        >
          {renderPanel({ expanded: true, panelTestId: `${testId}-dialog` })}
        </Dialog>
      ) : null}
    </>
  )
}
